import { styled } from '@porsche-code/kado-styled-system/jsx';
import { type ComponentProps, forwardRef } from 'react';

import { Tooltip } from '../Tooltip';

const ToolbarButtonRoot = styled('button', {
  base: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'xl',
    color: 'text.primary',
    cursor: 'pointer',
    height: '1.5rem',
    width: '1.5rem',
    fontSize: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&:hover:not([disabled])': {
      backgroundColor: 'background.frostedStrong',
    },

    '&[disabled]': {
      color: 'text.contrastLow',
      cursor: 'unset',
    },
  },

  variants: {
    active: {
      true: {
        backgroundColor: 'background.frosted',

        '&[disabled]': {
          color: 'text.contrastLow',
          cursor: 'unset',
        },
      },
    },
  },
});

type ToolbarButtonProps = ComponentProps<typeof ToolbarButtonRoot> & {
  tooltip?: string;
};

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ tooltip, ...props }, ref) => {
    const resolvedTooltip =
      tooltip ??
      (typeof props['aria-label'] === 'string'
        ? props['aria-label']
        : undefined);
    const button = (
      <ToolbarButtonRoot ref={ref} {...props} aria-label={resolvedTooltip} />
    );

    if (!resolvedTooltip) {
      return button;
    }

    return <Tooltip content={resolvedTooltip}>{button}</Tooltip>;
  },
);

ToolbarButton.displayName = 'ToolbarButton';
